import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { TRADES } from "@/components/Sections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREAS = ["Sliema", "St Julian's", "Valletta", "Gozo", "Somewhere else"];

const TERMS = [
  ["Commission, first 5 jobs or 60 days after your first job — whichever comes first", "0%"],
  ["Registration fee", "None"],
  ["Exclusivity", "None"],
  ["We call you back within", "48 hours"],
];

const CONFIRMATION = {
  tasker:
    "Thank you. Your details are with us. A member of the Sorted team will call your WhatsApp number within 48 hours to talk through your business — no obligation on your side.",
  client:
    "Thank you. We will email you once Sorted opens in Malta. Nothing is bookable yet.",
};

const EMPTY = { name: "", email: "", area: AREAS[0], whatsapp: "", service: TRADES[0] };

export const ApplySection = () => {
  const [tab, setTab] = useState("tasker");
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const switchTab = (t) => {
    setTab(t);
    setDone(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/submissions`, {
        type: tab,
        name: form.name,
        email: form.email,
        area: form.area,
        whatsapp: tab === "tasker" ? form.whatsapp : null,
        service: tab === "tasker" ? form.service : null,
      });
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setError("Something went wrong sending your details. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section" id="apply" data-testid="apply-section">
      <div className="wrap apply-grid">
        <Reveal>
          <div>
            <p className="eyebrow">Founding tasker intake</p>
            <h2 className="section-title" style={{ marginBottom: 26 }}>
              Join the founding list
            </h2>
            <p className="lede">
              Leave your details and we will call you within 48 hours. Registering
              costs nothing, commits you to nothing, and does not mean a job is
              waiting.
            </p>
            <dl className="terms-list" data-testid="terms-list">
              {TERMS.map(([k, v]) => (
                <div className="term-row" key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="form-card" data-testid="apply-form-card">
            <div className="form-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "tasker"}
                className={tab === "tasker" ? "form-tab active" : "form-tab"}
                onClick={() => switchTab("tasker")}
                data-testid="tab-tasker"
              >
                I want to work
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "client"}
                className={tab === "client" ? "form-tab active" : "form-tab"}
                onClick={() => switchTab("client")}
                data-testid="tab-client"
              >
                I need help
              </button>
            </div>
            <div className="form-body">
              {done ? (
                <div className="confirm-panel" data-testid="confirmation-panel">
                  <p className="confirm-label">Registered</p>
                  <p>{CONFIRMATION[tab]}</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="apply-form">
                  {error && (
                    <p className="form-error" data-testid="form-error">{error}</p>
                  )}
                  <div className="field">
                    <label htmlFor="f-name">Full name</label>
                    <input
                      id="f-name"
                      data-testid="field-name"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Joseph Camilleri"
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="f-email">Email</label>
                    <input
                      id="f-email"
                      type="email"
                      data-testid="field-email"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="f-area">Area in Malta</label>
                    <select
                      id="f-area"
                      data-testid="field-area"
                      value={form.area}
                      onChange={set("area")}
                    >
                      {AREAS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                  {tab === "tasker" && (
                    <>
                      <div className="field">
                        <label htmlFor="f-whatsapp">WhatsApp number</label>
                        <input
                          id="f-whatsapp"
                          data-testid="field-whatsapp"
                          value={form.whatsapp}
                          onChange={set("whatsapp")}
                          placeholder="+356 …"
                          required
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="f-service">Service you offer</label>
                        <select
                          id="f-service"
                          data-testid="field-service"
                          value={form.service}
                          onChange={set("service")}
                        >
                          {TRADES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <button
                    type="submit"
                    className="btn-brass"
                    style={{ width: "100%" }}
                    disabled={sending}
                    data-testid="submit-button"
                  >
                    {sending ? "Sending…" : "Send my details"}
                  </button>
                  <p className="form-smallprint" data-testid="form-smallprint">
                    We use your details only to contact you about Sorted in Malta.
                    No third parties, and you can ask us to delete them at any time.
                  </p>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
